<template>
  <MainLayout :initialDrawerExpanded="false" contentClass="pa-md-4" :has-sidebar="false">
    <template #header>
      <div class="overline">
        <PageBack />
      </div>
    </template>

    <template #content>
      <v-container fluid class="pa-0 pa-md-4">
        <v-row>
          <v-col cols="12" md="8" offset-md="2">

            <div v-if="loading" class="d-flex justify-center py-12">
              <v-progress-circular indeterminate color="primary" />
            </div>

            <v-alert v-else-if="error" type="error" class="mb-4">{{ error }}</v-alert>

            <template v-else-if="session">

              <!-- Header row -->
              <div class="d-flex align-center flex-wrap mb-5" style="gap: 8px">
                <div>
                  <div class="subtitle-1 font-weight-medium">
                    Session #{{ session.id }}
                    <span class="grey--text font-weight-regular ml-1">— {{ session.user }}</span>
                  </div>
                  <div class="caption grey--text">{{ formatDate(session.created_at) }}</div>
                </div>
                <v-spacer />
                <v-chip
                  :color="session.outcome_type === 'project' ? 'primary' : session.outcome_type === 'conversation' ? 'teal' : 'grey'"
                  dark small
                >
                  <v-icon left x-small>
                    {{ session.outcome_type === 'project' ? 'mdi-briefcase-outline' : session.outcome_type === 'conversation' ? 'mdi-chat-outline' : 'mdi-cancel' }}
                  </v-icon>
                  {{ session.outcome_type === 'project' ? 'Projet' : session.outcome_type === 'conversation' ? 'Conversation' : 'Hors sujet' }}
                  <template v-if="session.project_id || session.conversation_id">
                    #{{ session.project_id || session.conversation_id }}
                  </template>
                </v-chip>
                <v-chip v-if="totalCost !== null" color="orange darken-1" dark small>
                  <v-icon left x-small>mdi-currency-usd</v-icon>
                  {{ formatCost(totalCost) }} total
                </v-chip>
              </div>

              <!-- Initial message bubble -->
              <v-card elevation="0" outlined class="mb-6 rounded-lg">
                <v-card-text class="d-flex align-start pa-4">
                  <v-avatar color="primary lighten-4" size="36" class="mr-3 flex-shrink-0">
                    <v-icon color="primary">mdi-account-outline</v-icon>
                  </v-avatar>
                  <div>
                    <div class="caption grey--text mb-1">Message initial</div>
                    <div class="body-2" style="white-space: pre-wrap; line-height: 1.6">{{ session.initial_message }}</div>
                  </div>
                </v-card-text>
              </v-card>

              <!-- Section label: Init -->
              <div class="overline grey--text mb-2 ml-1">Initialisation</div>

              <!-- Timeline -->
              <v-timeline dense align-top class="mb-4">
                <template v-for="(item, i) in timelineItems">

                  <!-- Turn separator -->
                  <v-timeline-item
                    v-if="item.type === 'turn-start'"
                    :key="'t' + i"
                    hide-dot
                    class="pb-1"
                  >
                    <div class="overline grey--text mt-4">Tour {{ item.turn }}</div>
                  </v-timeline-item>

                  <!-- Decision badge -->
                  <v-timeline-item
                    v-else-if="item.type === 'decision'"
                    :key="'d' + i"
                    color="grey lighten-2"
                    small
                    class="pb-1"
                  >
                    <template #icon>
                      <v-icon x-small color="grey darken-1">mdi-arrow-down</v-icon>
                    </template>
                    <div class="d-flex align-center" style="gap: 6px; min-height: 24px">
                      <span class="caption grey--text">{{ item.label }}</span>
                      <v-chip x-small :color="stageColor(item.value)" dark>{{ item.value }}</v-chip>
                    </div>
                  </v-timeline-item>

                  <!-- Usage record accordion -->
                  <v-timeline-item
                    v-else-if="item.type === 'usage'"
                    :key="'u' + i"
                    :color="callTypeColor(item.call_type)"
                    fill-dot
                  >
                    <template #icon>
                      <v-icon x-small color="white">{{ callTypeIcon(item.call_type) }}</v-icon>
                    </template>
                    <v-expansion-panels flat class="mb-2" style="border: 1px solid rgba(0,0,0,0.12); border-radius: 8px; overflow: hidden">
                      <v-expansion-panel>
                        <v-expansion-panel-header class="pa-3" style="min-height: unset">
                          <div>
                            <div class="d-flex align-center flex-wrap" style="gap: 6px">
                              <span class="body-2 font-weight-medium">{{ callTypeLabel(item.call_type) }}</span>
                              <v-chip x-small label outlined color="grey darken-2" class="font-weight-mono">
                                {{ item.model }}
                              </v-chip>
                              <v-chip v-if="item.engine_name" x-small label color="blue-grey darken-1" dark>
                                <v-icon x-small left>mdi-cog-outline</v-icon>
                                {{ item.engine_name }}
                              </v-chip>
                              <v-chip v-if="item.vector_store_used" x-small color="cyan darken-1" dark>
                                <v-icon x-small left>mdi-database-search-outline</v-icon>
                                vector store
                              </v-chip>
                              <span class="caption grey--text ml-auto">{{ formatTime(item.created_at) }}</span>
                            </div>
                            <div class="d-flex align-center mt-1" style="gap: 16px">
                              <div class="d-flex align-center caption grey--text">
                                <v-icon x-small class="mr-1" color="grey">mdi-upload-outline</v-icon>
                                {{ item.prompt_tokens.toLocaleString() }}
                              </div>
                              <div class="d-flex align-center caption grey--text">
                                <v-icon x-small class="mr-1" color="grey">mdi-download-outline</v-icon>
                                {{ item.completion_tokens.toLocaleString() }}
                              </div>
                              <div
                                class="caption font-weight-medium"
                                :class="item.cost ? 'orange--text text--darken-2' : 'grey--text'"
                              >
                                {{ item.cost ? formatCost(parseFloat(item.cost)) : '—' }}
                              </div>
                            </div>
                          </div>
                        </v-expansion-panel-header>
                        <v-expansion-panel-content>
                          <v-divider />
                          <div class="pa-3" style="display: grid; gap: 16px">

                            <!-- Schema -->
                            <div v-if="item.schema_json">
                              <v-expansion-panels flat>
                                <v-expansion-panel style="border: 1px solid rgba(0,0,0,0.08); border-radius: 6px; overflow: hidden">
                                  <v-expansion-panel-header class="py-2 px-3 caption font-weight-medium" style="min-height: unset">
                                    <div class="d-flex align-center" style="gap: 6px">
                                      <v-icon x-small color="grey darken-1">mdi-code-json</v-icon>
                                      Schema
                                    </div>
                                  </v-expansion-panel-header>
                                  <v-expansion-panel-content>
                                    <v-divider />
                                    <div
                                      class="caption grey darken-3 white--text pa-2"
                                      style="white-space: pre; font-family: monospace; overflow-x: auto; line-height: 1.5"
                                    >{{ formatSchema(item.schema_json) }}</div>
                                  </v-expansion-panel-content>
                                </v-expansion-panel>
                              </v-expansion-panels>
                            </div>

                            <!-- System prompt -->
                            <div v-if="item.system_prompt_text">
                              <div class="overline grey--text mb-1">System prompt</div>
                              <div
                                class="caption grey darken-3 white--text rounded pa-2"
                                style="white-space: pre-wrap; font-family: monospace; max-height: 200px; overflow-y: auto; line-height: 1.5"
                              >{{ item.system_prompt_text }}</div>
                            </div>

                            <!-- Prompt (user message sent) -->
                            <div v-if="item.prompt_text">
                              <div class="overline grey--text mb-1">Prompt</div>
                              <div
                                class="caption grey lighten-4 rounded pa-2"
                                style="white-space: pre-wrap; max-height: 150px; overflow-y: auto; line-height: 1.5"
                              >{{ item.prompt_text }}</div>
                            </div>

                            <!-- Reply -->
                            <div v-if="item.reply_text">
                              <div class="overline grey--text mb-1">Réponse</div>
                              <div
                                class="caption grey lighten-4 rounded pa-2"
                                style="white-space: pre; font-family: monospace; max-height: 200px; overflow-y: auto; overflow-x: auto; line-height: 1.5"
                              >{{ formatReply(item.reply_text) }}</div>
                            </div>

                            <div v-if="!item.system_prompt_text && !item.prompt_text && !item.reply_text" class="caption grey--text">
                              Aucun détail disponible pour cet appel (enregistré avant la mise à jour).
                            </div>
                          </div>
                        </v-expansion-panel-content>
                      </v-expansion-panel>
                    </v-expansion-panels>
                  </v-timeline-item>

                  <!-- Qualification answers (inline after sector_qualify) -->
                  <v-timeline-item
                    v-else-if="item.type === 'qualification'"
                    :key="'q' + i"
                    color="orange darken-1"
                    fill-dot
                    class="pb-1"
                  >
                    <template #icon>
                      <v-icon x-small color="white">mdi-clipboard-check-outline</v-icon>
                    </template>
                    <v-card elevation="0" outlined class="mb-2 rounded-lg">
                      <v-card-text class="pa-3">
                        <div class="overline grey--text mb-2">Qualification Secteur</div>
                        <div style="display: grid; gap: 12px">
                          <div v-for="q in qualificationAnswers" :key="q.key">
                            <div class="caption grey--text mb-1">{{ q.label }}</div>
                            <div v-if="q.type === 'select'" class="d-flex flex-wrap" style="gap: 6px">
                              <v-chip
                                v-for="opt in q.options"
                                :key="opt"
                                x-small
                                :color="opt === q.answer ? 'primary' : ''"
                                :dark="opt === q.answer"
                                :outlined="opt !== q.answer"
                              >{{ opt }}</v-chip>
                              <v-chip
                                v-if="q.answer && !q.options.includes(q.answer)"
                                x-small color="primary" dark
                              >{{ q.answer }}</v-chip>
                              <span v-if="!q.answer" class="caption grey--text">—</span>
                            </div>
                            <div v-else>
                              <v-chip x-small :color="q.answer !== null ? 'primary' : ''" :dark="q.answer !== null" :outlined="q.answer !== null">
                                {{ q.answer !== null ? `${q.answer}${q.unit ? ' ' + q.unit : ''}` : '—' }}
                              </v-chip>
                            </div>
                          </div>
                        </div>
                      </v-card-text>
                    </v-card>
                  </v-timeline-item>

                  <!-- Outcome node -->
                  <v-timeline-item
                    v-else-if="item.type === 'outcome'"
                    :key="'o' + i"
                    :color="item.outcome_type === 'project' ? 'primary' : item.outcome_type === 'conversation' ? 'teal' : 'grey'"
                    fill-dot
                  >
                    <template #icon>
                      <v-icon x-small color="white">{{ item.outcome_type === 'other' ? 'mdi-cancel' : 'mdi-check' }}</v-icon>
                    </template>
                    <v-card
                      elevation="0"
                      :color="item.outcome_type === 'project' ? 'primary' : item.outcome_type === 'conversation' ? 'teal' : 'grey'"
                      dark
                      class="mb-2 rounded-lg"
                    >
                      <v-card-text class="pa-3 d-flex align-center">
                        <v-icon small class="mr-2">
                          {{ item.outcome_type === 'project' ? 'mdi-briefcase-outline' : item.outcome_type === 'conversation' ? 'mdi-chat-outline' : 'mdi-cancel' }}
                        </v-icon>
                        <span class="body-2 font-weight-medium">
                          {{ item.outcome_type === 'project'
                            ? `Projet #${item.id} créé`
                            : item.outcome_type === 'conversation'
                              ? `Conversation #${item.id} créée`
                              : 'Hors sujet — aucune ressource créée' }}
                        </span>
                        <v-spacer />
                        <v-btn
                          v-if="item.outcome_type === 'project' && item.id"
                          icon small
                          :to="{ name: 'project_detail', params: { id: item.id } }"
                        >
                          <v-icon small>mdi-open-in-new</v-icon>
                        </v-btn>
                      </v-card-text>
                    </v-card>
                  </v-timeline-item>

                </template>
              </v-timeline>

              <!-- Cost summary footer -->
              <v-divider class="mb-4" />
              <div class="d-flex justify-end align-center" style="gap: 16px">
                <div v-for="row in costSummary" :key="row.call_type" class="text-right">
                  <div class="caption grey--text">{{ callTypeLabel(row.call_type) }}</div>
                  <div class="body-2 font-weight-medium">{{ formatCost(row.cost) }}</div>
                </div>
                <v-divider vertical class="mx-2" style="height: 32px" />
                <div class="text-right">
                  <div class="caption grey--text">Total</div>
                  <div class="body-1 font-weight-bold orange--text text--darken-1">
                    {{ totalCost !== null ? formatCost(totalCost) : '—' }}
                  </div>
                </div>
              </div>

              <!-- Material table changes: adds, removals, substitutions (project sessions only) -->
              <AdminSessionMaterialChanges
                v-if="session.outcome_type === 'project' && session.project_id"
                :changes="session.material_changes || []"
                class="mt-6"
              />

              <!-- Brief feedback (project sessions only) -->
              <BriefFeedbackList
                v-if="session.outcome_type === 'project' && session.project_id"
                :project-id="session.project_id"
                class="mt-6"
              />

              <!-- QA Analysis panel -->
              <AdminAgentSessionQA :session-id="session.id" class="mt-6" />

            </template>

          </v-col>
        </v-row>
      </v-container>
    </template>
  </MainLayout>
</template>

<script>
import PageBack from '@/components/ui/PageBack.vue'
import MainLayout from '@/layout/MainLayout.vue'
import BriefFeedbackList from '@/components/chat/BriefFeedbackList.vue'
import AdminAgentSessionQA from '@/components/admin/AdminAgentSessionQA.vue'
import AdminSessionMaterialChanges from '@/components/admin/AdminSessionMaterialChanges.vue'
import { fetchAdminAgentSession } from '@/services/adminApi'

const CALL_TYPE_META = {
  classifier_stage1: {
    label: 'Classification Secteur',
    color: 'indigo',
    icon: 'mdi-filter-variant',
  },
  classifier_stage2: {
    label: 'Classification Interaction',
    color: 'blue darken-1',
    icon: 'mdi-swap-horizontal-circle-outline',
  },
  sector_qualify: {
    label: 'Qualification Secteur',
    color: 'orange darken-1',
    icon: 'mdi-clipboard-check-outline',
  },
  sector_agent: {
    label: 'Réponse Secteur',
    color: 'teal darken-1',
    icon: 'mdi-robot-outline',
  },
  sector_brief: {
    label: 'Génération Brief',
    color: 'deep-purple',
    icon: 'mdi-file-document-edit-outline',
  },
  generate_title: {
    label: 'Génération Titre',
    color: 'blue-grey darken-1',
    icon: 'mdi-format-title',
  },
  lookup: {
    label: 'Recherche Catalogue',
    color: 'cyan darken-1',
    icon: 'mdi-magnify',
  },
  direct_chat: {
    label: 'Chat Direct',
    color: 'green darken-1',
    icon: 'mdi-chat-processing-outline',
  },
}

const STAGE_COLORS = {
  isolation: 'orange darken-1',
  sols: 'brown darken-1',
  other: 'grey',
  discussion: 'teal',
  quantitatif: 'blue darken-1',
}

export default {
  name: 'AdminAgentSessionDetail',
  components: { PageBack, MainLayout, BriefFeedbackList, AdminAgentSessionQA, AdminSessionMaterialChanges },

  data() {
    return {
      loading: false,
      error: null,
      session: null,
    }
  },

  computed: {
    timelineItems() {
      if (!this.session) return []
      const items = []
      const { stage1, stage2, usage_records, routing_records, outcome_type, project_id, conversation_id } = this.session

      for (const u of usage_records) {
        items.push({ type: 'usage', ...u })
        if (u.call_type === 'classifier_stage1' && stage1) {
          items.push({ type: 'decision', label: 'Secteur →', value: stage1 })
        }
        if (u.call_type === 'classifier_stage2' && stage2) {
          items.push({ type: 'decision', label: 'Interaction →', value: stage2 })
        }
        if (u.call_type === 'sector_qualify') {
          items.push({ type: 'qualification' })
        }
      }

      if (outcome_type) {
        items.push({ type: 'outcome', outcome_type, id: project_id || conversation_id })
      }

      routing_records.forEach((rr, idx) => {
        items.push({ type: 'turn-start', turn: idx + 1, created_at: rr.created_at })
        if (rr.stage1) items.push({ type: 'decision', label: 'Secteur →', value: rr.stage1 })
        if (rr.stage2) items.push({ type: 'decision', label: 'Interaction →', value: rr.stage2 })
        for (const u of rr.usage_records) {
          items.push({ type: 'usage', ...u })
        }
      })

      return items
    },

    allUsageRecords() {
      if (!this.session) return []
      return [
        ...this.session.usage_records,
        ...this.session.routing_records.flatMap(rr => rr.usage_records),
      ]
    },

    totalCost() {
      const costs = this.allUsageRecords
        .filter(u => u.cost !== null)
        .map(u => parseFloat(u.cost))
      if (!costs.length) return null
      return costs.reduce((s, c) => s + c, 0)
    },

    costSummary() {
      const map = {}
      for (const u of this.allUsageRecords) {
        if (u.cost === null) continue
        if (!map[u.call_type]) map[u.call_type] = 0
        map[u.call_type] += parseFloat(u.cost)
      }
      return Object.entries(map).map(([call_type, cost]) => ({ call_type, cost }))
    },

    qualifyRecord() {
      return this.allUsageRecords.find(u => u.call_type === 'sector_qualify') || null
    },

    briefRecord() {
      return this.allUsageRecords.find(u => u.call_type === 'sector_brief') || null
    },

    qualificationAnswers() {
      if (!this.qualifyRecord?.reply_text) return []
      let questions = []
      try {
        const data = JSON.parse(this.qualifyRecord.reply_text)
        questions = data?.project?.questions || []
      } catch { return [] }
      if (!questions.length) return []

      const answers = {}
      if (this.briefRecord?.prompt_text) {
        const marker = '=== Réponses de l\'utilisateur ==='
        const idx = this.briefRecord.prompt_text.indexOf(marker)
        if (idx !== -1) {
          const section = this.briefRecord.prompt_text.slice(idx + marker.length).trim()
          for (const line of section.split('\n')) {
            const trimmed = line.trim()
            if (!trimmed) continue
            const sepIdx = trimmed.indexOf(' : ')
            if (sepIdx !== -1) {
              answers[trimmed.slice(0, sepIdx).trim()] = trimmed.slice(sepIdx + 3).trim()
            }
          }
        }
      }

      return questions.map(q => ({ ...q, answer: answers[q.label] ?? null }))
    },
  },

  mounted() {
    this.load()
  },

  methods: {
    async load() {
      this.loading = true
      this.error = null
      try {
        this.session = await fetchAdminAgentSession(this.$route.params.id)
      } catch {
        this.error = 'Impossible de charger la session.'
      } finally {
        this.loading = false
      }
    },

    callTypeColor(callType) {
      return (CALL_TYPE_META[callType] || {}).color || 'grey'
    },

    callTypeIcon(callType) {
      return (CALL_TYPE_META[callType] || {}).icon || 'mdi-help-circle-outline'
    },

    callTypeLabel(callType) {
      return (CALL_TYPE_META[callType] || {}).label || callType
    },

    stageColor(value) {
      return STAGE_COLORS[value] || 'grey darken-1'
    },

    formatDate(iso) {
      if (!iso) return '—'
      return new Date(iso).toLocaleString('fr-FR', { dateStyle: 'medium', timeStyle: 'short' })
    },

    formatTime(iso) {
      if (!iso) return ''
      const d = new Date(iso)
      const base = d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
      const ms = String(d.getMilliseconds()).padStart(3, '0')
      return `${base}.${ms}`
    },

    formatCost(val) {
      if (val === null || val === undefined) return '—'
      return `$${val.toFixed(6)}`
    },

    formatSchema(schemaJson) {
      try {
        return JSON.stringify(JSON.parse(schemaJson), null, 2)
      } catch {
        return schemaJson
      }
    },

    formatReply(text) {
      try {
        return JSON.stringify(JSON.parse(text), null, 2)
      } catch {
        return text
      }
    },

  },
}
</script>
